<template>
  <div class="masonry-grid" :style="gridStyle">
    <div
      v-for="(column, columnIndex) in balancedColumns"
      :key="`column-${columnIndex}`"
      class="masonry-column"
    >
      <div v-for="item in column" :key="item[itemKey]" class="masonry-item">
        <slot :item="item" />
      </div>
    </div>
  </div>
</template>

<script>
/**
 * MasonryGrid Component
 *
 * An auto-balancing masonry grid that distributes items across columns
 * to create visually balanced layouts. Uses a greedy algorithm to always
 * add the next item to the column with the smallest total height.
 *
 * @example
 * <masonry-grid :items="categories" :column-count="3" item-key="id">
 *   <template #default="{ item }">
 *     <div>{{ item.title }}</div>
 *   </template>
 * </masonry-grid>
 */
export default {
  name: "MasonryGrid",
  props: {
    /**
     * Array of items to distribute across columns
     */
    items: {
      type: Array,
      required: true,
      default: () => [],
    },
    /**
     * Number of columns to create
     */
    columnCount: {
      type: Number,
      default: 3,
      validator: (value) => value > 0,
    },
    /**
     * Gap between columns (CSS value)
     */
    columnGap: {
      type: String,
      default: "24px",
    },
    /**
     * Key to use for item identification in v-for
     */
    itemKey: {
      type: String,
      default: "id",
    },
    /**
     * Function to estimate item height
     * Receives item as parameter, returns estimated height (number)
     * If not provided, uses default estimation based on item.commands.length
     */
    heightEstimator: {
      type: Function,
      default: null,
    },
  },
  computed: {
    /**
     * Distribute items across columns using greedy algorithm
     * Always adds next item to the column with smallest total height
     */
    balancedColumns() {
      const numCols = this.columnCount;

      if (numCols <= 0 || !this.items || this.items.length === 0) {
        return [];
      }

      const columns = Array.from({ length: numCols }, () => []);
      const columnHeights = Array(numCols).fill(0);

      // Distribute items using greedy algorithm
      this.items.forEach((item) => {
        // Find column with minimum height
        const minHeight = Math.min(...columnHeights);
        const minHeightIndex = columnHeights.indexOf(minHeight);

        // Add item to shortest column
        columns[minHeightIndex].push(item);

        // Update column height
        const itemHeight = this.estimateHeight(item);
        columnHeights[minHeightIndex] += itemHeight;
      });

      return columns;
    },

    /**
     * Grid container styles
     */
    gridStyle() {
      return {
        display: "flex",
        gap: this.columnGap,
        width: "100%",
      };
    },
  },
  methods: {
    /**
     * Estimate the height of an item
     * Uses custom estimator if provided, otherwise uses default logic
     */
    estimateHeight(item) {
      if (this.heightEstimator && typeof this.heightEstimator === "function") {
        return this.heightEstimator(item);
      }

      if (item.commands && Array.isArray(item.commands)) {
        // Base height + (commands * height per command)
        const baseHeight = 80; // Header, padding, etc.
        const commandHeight = 40; // Approximate height per command
        return baseHeight + item.commands.length * commandHeight;
      }

      // Fallback: assume fixed height
      return 100;
    },
  },
};
</script>

<style lang="scss" scoped>
.masonry-grid {
  display: flex;
  align-items: flex-start;
}

.masonry-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0; /* Prevent flex item overflow */
}

.masonry-item {
  width: 100%;
}
</style>
